import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Modales } from '../Modales';
import ReprogramarProgramacion from './ReprogramarProgramacion';
import { reprogramarOt, actualizarDatos, getVisitaUltima, getMetrologos } from '../../api/common';

/**
 * Modal reutilizable para reprogramar una OT o Programacion.
 *
 * @param {boolean}  show        - Controla la visibilidad
 * @param {Function} onClose     - Callback al cerrar sin cambios
 * @param {Function} onSuccess   - Callback tras reprogramar exitosamente
 * @param {'ot'|'programacion'} entidad - Tipo de entidad a reprogramar
 * @param {string}   idEntidad   - _id de la entidad
 */
const ModalReprogramar = ({ show, onClose, onSuccess, entidad = 'programacion', idEntidad }) => {

  console.log('[ModalReprogramar] Props recibidas:', { show, entidad, idEntidad });
  const [tipoReprog, setTipoReprog] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [metrologos, setMetrologos] = useState([]);

  const [visitaActual, setVisitaActual] = useState(null);

  // Cargar metrólogos una sola vez al montar
  useEffect(() => {
    getMetrologos()
      .then((res) => {
        const lista = (res?.data.data || []).map((m) => ({
          value: m._id,
          label: `${m.nombre} ${m.apellido || ''}`.trim(),
        }));
        setMetrologos(lista);
      })
      .catch((err) => console.error('[ModalReprogramar] Error cargando metrólogos:', err));
  }, []);


useEffect(() => {
  if (!show) {
    setTipoReprog(null);
    setShowForm(false);
    setVisitaActual(null);
    return;
  }

  let efectoCancelado = false;

  console.count('[ModalReprogramar] Ejecución de Swal.fire');

  Swal.fire({
    title: 'Reprogramar — ¿qué deseas hacer en el calendario?',
    html: [
      '<div class="text-start">',
      '<p class="mb-2">Esta reprogramación afecta el calendario corporativo. Elige cómo registrar el cambio:</p>',
      '<ul>',
      '<li><b>Nueva visita:</b> Se crea una nueva entrada en el calendario. La visita anterior queda en el historial.</li>',
      '<li><b>Actualizar visita actual:</b> Se modifica la entrada existente. <span class="text-danger">Se pierde la trazabilidad de la programación anterior en el calendario.</span></li>',
      '</ul>',
      '</div>'
    ].join(''),
    icon: 'question',
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: 'Nueva visita',
    denyButtonText: 'Actualizar visita actual',
    cancelButtonText: 'Cancelar',
    confirmButtonColor: '#3085d6',
    denyButtonColor: '#e08000',
  }).then(async (result) => {

    // Ignorar resultados de una ejecución anterior del efecto
    if (efectoCancelado) {
      console.log(
        '[ModalReprogramar] Resultado de un efecto cancelado; se ignora.'
      );
      return;
    }

    console.log('[ModalReprogramar] Resultado de Swal:', result);

    if (result.isDismissed) {
      console.log('[ModalReprogramar] Usuario canceló la reprogramación.');
      onClose();
      return;
    }

    let tipo;

    if (result.isConfirmed) {
      tipo = 'nueva_visita';
    } else if (result.isDenied) {
      tipo = 'actualizar_visita';
    } else {
      return;
    }

    setTipoReprog(tipo);

    if (tipo === 'actualizar_visita' && idEntidad) {
      try {
        const param = entidad === 'ot'
          ? { otId: idEntidad }
          : { programacionId: idEntidad };

        const res = await getVisitaUltima(param);

        // Evitar actualizar estado si el efecto ya fue cancelado
        if (efectoCancelado) return;

        console.log('Respuesta de getVisitaUltima:', res);
        setVisitaActual(res?.data?.visita || null);

      } catch (err) {
        if (efectoCancelado) return;

        console.error(
          '[ModalReprogramar] Error cargando última visita:',
          err
        );

        setVisitaActual(null);
      }
    }

    if (!efectoCancelado) {
      setShowForm(true);
    }
  });

  // Limpiar el diálogo y evitar procesar resultados obsoletos
  return () => {
    efectoCancelado = true;

    if (Swal.isVisible()) {
      Swal.close();
    }
  };

// eslint-disable-next-line react-hooks/exhaustive-deps
}, [show]);



  const handleAceptar = async (data) => {
    try {
      if (entidad === 'ot') {
        await reprogramarOt(idEntidad, {
          fechaEntrega: data.fechaInicio,
          fechaCierre: data.fechaCierre,
          tipoReprogramacion: tipoReprog,
          responsablesId: data.responsablesId,
        });
      } else {
        await actualizarDatos('Programacion', {
          _id: idEntidad,
          fechaInicio: data.fechaInicio,
          fechaCierre: data.fechaCierre,
          tipoReprogramacion: tipoReprog,
          responsablesId: data.responsablesId,
        }, idEntidad);
      }
      Swal.fire('Reprogramada', 'La agenda ha sido reprogramada correctamente.', 'success');
      setShowForm(false);
      onSuccess();
    } catch (error) {
      console.error('[ModalReprogramar] Error al reprogramar:', error);
      Swal.fire('Error', 'Hubo un problema al reprogramar la agenda.', 'error');
    }
  };

  const handleCancelar = () => {
    setShowForm(false);
    onClose();
  };

  if (!showForm) return null;

  return (
    <Modales estado={showForm} cambiarestado={handleCancelar} titulo="Reprogramar">
      <ReprogramarProgramacion
        onAceptar={handleAceptar}
        onCancelar={handleCancelar}
        visitaActual={visitaActual}
        metrologos={metrologos}
      />
    </Modales>
  );
};

export default ModalReprogramar;
